import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table, Button, Tag, Spin, Empty, Drawer, Form, Input, message, Space, Badge, Tooltip, Typography
} from "antd";
import {
  FileProtectOutlined, EditOutlined, EyeOutlined, CheckCircleOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../../const";
import ComplianceDocViewer from "../../../components/ComplianceDocViewer/ComplianceDocViewer";

const { Text } = Typography;

const PageWrapper = styled.div`
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 4px 0; }
  p.subtitle { color: #666; font-size: 14px; margin: 0 0 24px 0; }
`;

const DocBadgeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const DOC_TYPES = ['nda', 'contract', 'declaration'];
const DOC_TYPE_LABELS = { nda: 'NDA', contract: 'Contract', declaration: 'Declaration' };
const DOC_TYPE_COLORS = { nda: 'blue', contract: 'purple', declaration: 'cyan' };

const ComplianceDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDoc, setViewDoc] = useState(null);
  const [signDrawerOpen, setSignDrawerOpen] = useState(false);
  const [signingDoc, setSigningDoc] = useState(null);
  const [signing, setSigning] = useState(false);
  const [form] = Form.useForm();

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.post(API_CONST.COMPLIANCE_DOCUMENTS_LIST, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(res.data.documents || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load compliance documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Group flat doc list into hiring-record-level rows
  const groupedRows = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => {
      if (!map.has(doc.hiringRecordId)) {
        map.set(doc.hiringRecordId, {
          hiringRecordId: doc.hiringRecordId,
          talentName: doc.talentName || '—',
          jobId: doc.jobId || '—',
          docs: {},
        });
      }
      map.get(doc.hiringRecordId).docs[doc.docType] = doc;
    });
    return Array.from(map.values());
  }, [documents]);

  const pendingSetCount = useMemo(
    () => groupedRows.filter((r) => DOC_TYPES.some((t) => r.docs[t]?.status === 'pending')).length,
    [groupedRows]
  );

  const openSignDrawer = async (doc) => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_CONST.COMPLIANCE_DOCUMENTS}/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const full = res.data.document;
      setSigningDoc(full);
      form.setFieldsValue({
        talentName: full.talentName || "",
        aadhaarNumber: full.aadhaarNumber || "",
        signedLocation: "",
      });
      setSignDrawerOpen(true);
    } catch (err) {
      message.error("Failed to load document details");
    }
  };

  const openViewDrawer = async (doc) => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_CONST.COMPLIANCE_DOCUMENTS}/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewDoc(res.data.document);
    } catch (err) {
      message.error("Failed to load document");
    }
  };

  const handleSign = async () => {
    try {
      const values = await form.validateFields();
      setSigning(true);
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.COMPLIANCE_DOCUMENTS}/${signingDoc.id}/sign`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Document signed successfully");
      setSignDrawerOpen(false);
      setSigningDoc(null);
      form.resetFields();
      fetchDocuments();
      // Notify PartnerDashboard to refresh the sidebar pending-badge count
      window.dispatchEvent(new Event('complianceSigned'));
    } catch (err) {
      if (err.response?.data?.error) {
        message.error(err.response.data.error);
      } else if (!err.errorFields) {
        message.error("Failed to sign document");
      }
    } finally {
      setSigning(false);
    }
  };

  // Render per-doc type actions within a hiring-record row
  const renderDocCell = (docType, row) => {
    const doc = row.docs[docType];
    if (!doc) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
    return (
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        {doc.status === 'signed' ? (
          <Tag icon={<CheckCircleOutlined />} color="success" style={{ marginBottom: 0 }}>Signed</Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="warning" style={{ marginBottom: 0 }}>Pending</Tag>
        )}
        {doc.status === 'pending' ? (
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openSignDrawer(doc)}
            style={{ background: "#014c75", borderColor: "#014c75", fontSize: 11 }}
          >
            Sign
          </Button>
        ) : (
          <Button size="small" icon={<EyeOutlined />} onClick={() => openViewDrawer(doc)} style={{ fontSize: 11 }}>
            View
          </Button>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: "Hiring Record",
      dataIndex: "hiringRecordId",
      key: "hiringRecordId",
      width: 170,
      render: (id) => <Text style={{ color: "#014c75", fontWeight: 500, fontSize: 13 }}>{id}</Text>,
    },
    {
      title: "Talent",
      dataIndex: "talentName",
      key: "talentName",
      width: 150,
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 180,
      render: (id) => <Text style={{ color: "#014c75", fontSize: 13 }}>{id || '—'}</Text>,
    },
    {
      title: () => <Tag color="blue">NDA</Tag>,
      key: "nda",
      width: 110,
      render: (_, row) => renderDocCell('nda', row),
    },
    {
      title: () => <Tag color="purple">Contract</Tag>,
      key: "contract",
      width: 110,
      render: (_, row) => renderDocCell('contract', row),
    },
    {
      title: () => <Tag color="cyan">Declaration</Tag>,
      key: "declaration",
      width: 120,
      render: (_, row) => renderDocCell('declaration', row),
    },
    {
      title: "Set Status",
      key: "setStatus",
      width: 130,
      render: (_, row) => {
        const pending = DOC_TYPES.filter((t) => row.docs[t]?.status === 'pending').length;
        const signed = DOC_TYPES.filter((t) => row.docs[t]?.status === 'signed').length;
        if (pending === 0) return <Tag color="success">All Signed</Tag>;
        return <Tag color="warning">{pending} Pending</Tag>;
      },
    },
  ];

  return (
    <PageWrapper>
      <h1>
        Compliance Documents{" "}
        {pendingSetCount > 0 && (
          <Tooltip title={`${pendingSetCount} hiring record(s) with unsigned documents`}>
            <Badge count={pendingSetCount} style={{ marginLeft: 8, background: "#ff4d4f" }} />
          </Tooltip>
        )}
      </h1>
      <p className="subtitle">Sign and track NDA, Contract, and Declaration for each placed talent. Each hiring record has 3 documents.</p>

      <Spin spinning={loading}>
        {groupedRows.length > 0 ? (
          <Table
            columns={columns}
            dataSource={groupedRows}
            rowKey="hiringRecordId"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total) => `${total} hiring record(s)`,
            }}
            scroll={{ x: 1000 }}
          />
        ) : (
          <Empty
            description={
              loading ? "Loading..." : (
                <span>
                  No compliance documents yet.{" "}
                  Documents (NDA, Contract, Declaration) are generated automatically
                  when a talent from your organisation is placed and a hiring record is created.
                </span>
              )
            }
          />
        )}
      </Spin>

      <Drawer
        open={signDrawerOpen}
        onClose={() => { setSignDrawerOpen(false); setSigningDoc(null); form.resetFields(); }}
        title={
          <Space>
            <FileProtectOutlined />
            <span>Sign Document: {DOC_TYPE_LABELS[signingDoc?.docType]}</span>
          </Space>
        }
        width={720}
        extra={
          <Button
            type="primary"
            loading={signing}
            onClick={handleSign}
            style={{ background: "#014c75", borderColor: "#014c75" }}
          >
            Submit & Sign
          </Button>
        }
      >
        {signingDoc && (
          <>
            <div
              style={{
                border: "1px solid #e8e8e8",
                borderRadius: 6,
                maxHeight: 340,
                overflowY: "auto",
                marginBottom: 24,
              }}
            >
              <ComplianceDocViewer
                document={signingDoc}
                open={true}
                onClose={() => {}}
                mode="inline"
              />
            </div>

            <div
              style={{
                background: "#fffbf0",
                border: "1px solid #ffe58f",
                borderRadius: 6,
                padding: "12px 16px",
                marginBottom: 20,
                fontSize: 13,
                color: "#7c5900",
              }}
            >
              By entering the talent's name below and clicking "Submit & Sign", you confirm that the talent has reviewed and consents to this document. This serves as a digital acknowledgement.
            </div>

            <Form form={form} layout="vertical">
              <Form.Item
                name="talentName"
                label="Talent Full Name (Digital Signature)"
                rules={[{ required: true, message: "Talent name is required" }]}
              >
                <Input placeholder="Enter talent's full name exactly as it appears in their profile" />
              </Form.Item>
              <Form.Item
                name="aadhaarNumber"
                label="Talent Aadhaar Number"
                rules={[{ required: true, message: "Aadhaar number is required" }]}
              >
                <Input placeholder="e.g. 1234 5678 9012" maxLength={20} />
              </Form.Item>
              <Form.Item
                name="signedLocation"
                label="Location of Signing"
                rules={[{ required: true, message: "Location is required" }]}
              >
                <Input placeholder="e.g. Mumbai, Maharashtra" />
              </Form.Item>
              <Form.Item label="Date of Signing">
                <Input
                  value={new Date().toLocaleDateString("en-GB", {
                    day: "2-digit", month: "long", year: "numeric",
                  })}
                  disabled
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Drawer>

      {viewDoc && (
        <ComplianceDocViewer
          document={viewDoc}
          open={!!viewDoc}
          onClose={() => setViewDoc(null)}
          mode="drawer"
        />
      )}
    </PageWrapper>
  );
};

export default ComplianceDocuments;
